import type { AxiosResponse, AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios'

export interface ParsedDisposition {
  disposition: string | null
  filename: string | null
}

export default class HttpResponse<T = any> {
  private readonly _headers: Record<string, string>
  private readonly _data: T
  private readonly _status: number = 200

  constructor(axiosResponse: AxiosResponse<T>) {
    this._headers = this.safeNormalizeHeaders(axiosResponse.headers)
    this._data = axiosResponse.data
    this._status = axiosResponse.status
  }

  get headers(): Record<string, string> {
    return { ...this._headers } // Copie défensive
  }

  get data(): T {
    return this._data
  }

  get status(): number {
    return this._status
  }

  getHeader(name: string): string | undefined {
    return this._headers[name.toLowerCase()]
  }

  hasHeader(name: string): boolean {
    return name.toLowerCase() in this._headers
  }

  getHeadersMatching(pattern: RegExp): Record<string, string> {
    return Object.entries(this._headers)
      .filter(([key]) => pattern.test(key))
      .reduce(
        (acc, [key, value]) => {
          acc[key] = value
          return acc
        },
        {} as Record<string, string>
      )
  }

  parseDisposition(): ParsedDisposition {
    const header = this.getHeader('content-disposition')
    if (!header) return { disposition: null, filename: null }

    // ex: attachment; filename="report.pdf"; filename*=UTF-8''report%20é.pdf
    const [dispositionPart, ...params] = header.split(';').map((s: any) => s.trim())

    let filename: string | null = null

    // Cherche d'abord filename*= (UTF-8 encodé)
    const utf8Param = params.find((p: any) => p.toLowerCase().startsWith('filename*='))
    if (utf8Param) {
      const match = utf8Param.match(/filename\*=\s*UTF-8''(.+)/i)
      if (match?.[1]) {
        filename = decodeURIComponent(match[1])
      }
    }

    // Sinon fallback sur filename="..."
    if (!filename) {
      const asciiParam = params.find((p: any) => p.toLowerCase().startsWith('filename='))
      if (asciiParam) {
        const match = asciiParam.match(/filename="?([^"]+)"?/)
        if (match?.[1]) {
          filename = match[1]
        }
      }
    }

    return {
      disposition: dispositionPart || null,
      filename
    }
  }

  private safeNormalizeHeaders(
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders
  ): Record<string, string> {
    try {
      if (headers === undefined || headers === null) return {}
      return Object.entries(headers).reduce((acc, [key, value]) => {
        if (key && value !== undefined && value !== null) {
          const normalizedKey = key.toLowerCase().trim();
          let normalizedValue: string;

          if (Array.isArray(value)) {
            // Prendre le premier élément si array
            normalizedValue = String(value[0] || '');
          } else {
            normalizedValue = String(value);
          }

          acc[normalizedKey] = normalizedValue;
        }
        return acc;
      }, {} as Record<string, string>);

    } catch (error) {
      console.warn('Error normalizing headers:', error);
      return {};
    }
  }

  static from<T>(headers: Record<string, string>, data: T) {
    return new this({headers, data} as AxiosResponse<T>)
  }
}